import { useCallback, useState } from "react";
import moment from "moment";
import { useContextContrato } from "src/Contexts/Lancamentos/Contrato/ContratosContext";
import { TQuickFilterId } from "./QuickFiltersParcelas";

export interface IQuickFilterParams {
  startDueDate?: string;
  endDueDate?: string;
  startDateReceipt?: string;
  endDateReceipt?: string;
  installmentReceipt?: "TRUE" | "FALSE" | "ALL";
}

export const useQuickFiltersParcelas = () => {
  const { setValuesFilterBordero, valuesFilterBordero } = useContextContrato();
  const [activeFilters, setActiveFilters] = useState<TQuickFilterId[]>([]);

  const getMonthDates = useCallback(() => {
    const startOfMonth = moment().startOf("month").format("YYYY-MM-DD");
    const endOfMonth = moment().endOf("month").format("YYYY-MM-DD");
    const today = moment().format("YYYY-MM-DD");
    const yesterday = moment().subtract(1, "day").format("YYYY-MM-DD");
    const thirtyDaysAgo = moment().subtract(30, "days").format("YYYY-MM-DD");

    return { startOfMonth, endOfMonth, today, yesterday, thirtyDaysAgo };
  }, []);

  const getSingleFilterParams = useCallback((filterId: TQuickFilterId): IQuickFilterParams => {
    const { startOfMonth, endOfMonth, today, yesterday, thirtyDaysAgo } = getMonthDates();

    switch (filterId) {
      case "vencimento_mes":
        return {
          startDueDate: startOfMonth,
          endDueDate: endOfMonth,
        };

      case "recebimento_mes":
        return {
          startDateReceipt: startOfMonth,
          endDateReceipt: endOfMonth,
          installmentReceipt: "TRUE",
        };

      case "vencido_mes":
        return {
          startDueDate: startOfMonth,
          endDueDate: today,
          installmentReceipt: "FALSE",
        };

      case "atrasado_sem_receber":
        return {
          startDueDate: thirtyDaysAgo,
          endDueDate: yesterday,
          installmentReceipt: "FALSE",
        };

      default:
        return {};
    }
  }, [getMonthDates]);

  const getCombinedFilterParams = useCallback((filters: TQuickFilterId[]): IQuickFilterParams => {
    if (filters.length === 0) {
      return {
        startDueDate: "",
        endDueDate: "",
        startDateReceipt: "",
        endDateReceipt: "",
        installmentReceipt: "ALL",
      };
    }

    const combinedParams: IQuickFilterParams = {
      startDueDate: "",
      endDueDate: "",
      startDateReceipt: "",
      endDateReceipt: "",
      installmentReceipt: "ALL",
    };

    let hasReceiptFilter = false;
    let requiresNotReceived = false;
    let requiresReceived = false;

    filters.forEach((filterId) => {
      const params = getSingleFilterParams(filterId);

      // Combinar datas de vencimento - pegar o range mais amplo
      if (params.startDueDate) {
        if (!combinedParams.startDueDate || params.startDueDate < combinedParams.startDueDate) {
          combinedParams.startDueDate = params.startDueDate;
        }
      }
      if (params.endDueDate) {
        if (!combinedParams.endDueDate || params.endDueDate > combinedParams.endDueDate) {
          combinedParams.endDueDate = params.endDueDate;
        }
      }

      // Combinar datas de recebimento - pegar o range mais amplo
      if (params.startDateReceipt) {
        if (!combinedParams.startDateReceipt || params.startDateReceipt < combinedParams.startDateReceipt) {
          combinedParams.startDateReceipt = params.startDateReceipt;
        }
      }
      if (params.endDateReceipt) {
        if (!combinedParams.endDateReceipt || params.endDateReceipt > combinedParams.endDateReceipt) {
          combinedParams.endDateReceipt = params.endDateReceipt;
        }
      }

      // Gerenciar status de recebimento
      if (params.installmentReceipt === "TRUE") {
        hasReceiptFilter = true;
        requiresReceived = true;
      }
      if (params.installmentReceipt === "FALSE") {
        hasReceiptFilter = true;
        requiresNotReceived = true;
      }
    });

    // Determinar o status de recebimento combinado
    if (hasReceiptFilter) {
      if (requiresReceived && requiresNotReceived) {
        // Se ambos são requeridos, mostrar todas
        combinedParams.installmentReceipt = "ALL";
      } else if (requiresReceived) {
        combinedParams.installmentReceipt = "TRUE";
      } else if (requiresNotReceived) {
        combinedParams.installmentReceipt = "FALSE";
      }
    }

    return combinedParams;
  }, [getSingleFilterParams]);

  const syncFilterToContext = useCallback((filterParams: IQuickFilterParams) => {
    setValuesFilterBordero({
      ...valuesFilterBordero,
      startDueDate: filterParams.startDueDate || "",
      endDueDate: filterParams.endDueDate || "",
      startDateReceipt: filterParams.startDateReceipt || "",
      endDateReceipt: filterParams.endDateReceipt || "",
      installmentReceipt: filterParams.installmentReceipt || "ALL",
    });
  }, [setValuesFilterBordero, valuesFilterBordero]);

  const toggleFilter = useCallback((filterId: TQuickFilterId): TQuickFilterId[] => {
    let newFilters: TQuickFilterId[];

    if (activeFilters.includes(filterId)) {
      // Remover o filtro
      newFilters = activeFilters.filter((f) => f !== filterId);
    } else {
      // Adicionar o filtro
      newFilters = [...activeFilters, filterId];
    }

    setActiveFilters(newFilters);

    // Sincronizar com o contexto
    const combinedParams = getCombinedFilterParams(newFilters);
    syncFilterToContext(combinedParams);

    return newFilters;
  }, [activeFilters, getCombinedFilterParams, syncFilterToContext]);

  const clearFilters = useCallback(() => {
    setActiveFilters([]);

    // Limpar os valores no contexto
    syncFilterToContext({
      startDueDate: "",
      endDueDate: "",
      startDateReceipt: "",
      endDateReceipt: "",
      installmentReceipt: "ALL",
    });
  }, [syncFilterToContext]);

  const getActiveFilterParams = useCallback((): IQuickFilterParams => {
    return getCombinedFilterParams(activeFilters);
  }, [activeFilters, getCombinedFilterParams]);

  return {
    activeFilters,
    setActiveFilters,
    toggleFilter,
    clearFilters,
    getCombinedFilterParams,
    getActiveFilterParams,
  };
};
